import { useState, useEffect } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Divider,
  Stack,
  TextField,
  MenuItem,
  FormControl,
  FormLabel,
  FormHelperText,
} from '@mui/material';
import { Edit2, AlignLeft, Save, X, ChevronDown, Activity, ClipboardList, StickyNote, Stethoscope, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '../../lib/api';
import { safeConsole } from '../../lib/piiProtection';
import VitalsAuditTooltip from './VitalsAuditTooltip';
import { IndicatorBadge, getVitalIndicator } from './vitalIndicators';
import { DynamicEncounterFields, encodeEncounterFieldValues } from '../common/DynamicEncounterFields';
import { useSpecialtyTemplate } from '../../hooks/useSpecialtyTemplate';

const EDIT_REASON_CATEGORIES = [
  { value: 'MEASUREMENT_ERROR', label: 'Measurement Error' },
  { value: 'INCORRECT_ENTRY', label: 'Incorrect Entry' },
  { value: 'RECHECKED', label: 'Rechecked' },
  { value: 'DATA_CORRECTION', label: 'Data Correction' },
];

interface VisitVitalsHistoryTabProps {
  patientId: string;
  appointmentStatus?: string;
  onLatestVitalsStatus?: (hasVitals: boolean) => void;
  /** Current doctor's id, used to resolve a specialty encounter template (extra vitals fields). */
  doctorId?: string;
}

interface VisitWithVitals {
  id: string;
  visitDate: string;
  doctorName: string;
  chiefComplaint?: string;
  duration?: string;
  systolicBP?: number;
  diastolicBP?: number;
  pulse?: number;
  temperature?: number;
  spO2?: number;
  respiratoryRate?: number;
  weight?: number;
  currentMedications?: string;
  clinicalNotes?: string;
  lastEditedByName?: string;
  lastEditedAt?: string;
  totalEdits?: number;
  structuredDataJson?: string;
}

/** Section label used above each detail group — replaces the old emoji + caption convention. */
function SectionLabel({ icon, label, color }: { icon: React.ReactNode; label: string; color: string }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.25 }}>
      <Box sx={{ width: 22, height: 22, borderRadius: 1, bgcolor: `${color}1A`, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </Box>
      <Typography variant="overline" sx={{ fontWeight: 700, color, letterSpacing: 0.5 }}>{label}</Typography>
    </Stack>
  );
}

/** One vital reading — colored by clinical indicator, matching the Active Encounter dashboard card. */
function VitalTile({
  visitId, field, label, value, unit,
}: {
  visitId: string; field: string; label: string; value: number; unit: string;
}) {
  const ind = getVitalIndicator(field, String(value));
  const color = ind?.color ?? '#0288d1';
  const bg = ind?.bg ?? '#e3f2fd';
  return (
    <Grid item xs={6} sm={3}>
      <VitalsAuditTooltip visitId={visitId} fieldName={field} label={label}>
        <Box sx={{ p: 1.25, bgcolor: bg, borderRadius: 1.5, cursor: 'help', height: '100%' }}>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700 }}>
            {label}
          </Typography>
          <Typography variant="h6" sx={{ color, fontWeight: 800, lineHeight: 1.3 }}>{value}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {unit}{ind ? ` · ${ind.label}` : ''}
          </Typography>
        </Box>
      </VitalsAuditTooltip>
    </Grid>
  );
}

/** True when this visit has no vitals recorded yet — a first-time entry needs no edit reason. */
function isInitialVitalsEntry(v: VisitWithVitals): boolean {
  return [
    v.systolicBP, v.diastolicBP, v.pulse, v.temperature, v.spO2, v.respiratoryRate, v.weight,
  ].every((val) => val === null || val === undefined);
}

/** Compact one-line vital chips for the collapsed visit summary (only present values). */
function compactVitals(v: VisitWithVitals): string[] {
  const out: string[] = [];
  if (v.systolicBP != null && v.diastolicBP != null) out.push(`BP ${v.systolicBP}/${v.diastolicBP}`);
  else if (v.systolicBP != null) out.push(`SBP ${v.systolicBP}`);
  else if (v.diastolicBP != null) out.push(`DBP ${v.diastolicBP}`);
  if (v.pulse != null) out.push(`Pulse ${v.pulse}`);
  if (v.temperature != null) out.push(`Temp ${v.temperature}°F`);
  if (v.spO2 != null) out.push(`SpO₂ ${v.spO2}%`);
  if (v.respiratoryRate != null) out.push(`RR ${v.respiratoryRate}`);
  if (v.weight != null) out.push(`Wt ${v.weight}kg`);
  return out;
}

export function VisitVitalsHistoryTab({ patientId, appointmentStatus, onLatestVitalsStatus, doctorId }: VisitVitalsHistoryTabProps) {
  const [vitalsHistory, setVitalsHistory] = useState<VisitWithVitals[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingVisitId, setEditingVisitId] = useState<string | null>(null);
  const [editingVisit, setEditingVisit] = useState<VisitWithVitals | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editErrors, setEditErrors] = useState<{[key: string]: string}>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [extraFieldValues, setExtraFieldValues] = useState<Record<string, string>>({});
  const { template: encounterTemplate } = useSpecialtyTemplate(doctorId);

  // Edit form state
  const [formData, setFormData] = useState({
    systolicBP: '',
    diastolicBP: '',
    pulse: '',
    temperature: '',
    spO2: '',
    respiratoryRate: '',
    weight: '',
    duration: '',
    currentMedications: '',
    clinicalNotes: '',
  });

  const [editReason, setEditReason] = useState({
    category: '',
    detail: '',
  });

  useEffect(() => {
    loadVitalsHistory();
  }, [patientId]);

  const loadVitalsHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.visits.getWithVitals(patientId);
      if (response.error) {
        throw new Error(response.error);
      }
      // Sort by date (newest first)
      const sorted = (response.data || []).sort(
        (a: any, b: any) => new Date(b.visitDate).getTime() - new Date(a.visitDate).getTime()
      );
      setVitalsHistory(sorted);
      // Auto-expand the most recent visit; older ones stay collapsed for a clean, scannable list.
      setExpandedId(sorted[0]?.id ?? null);
      if (onLatestVitalsStatus && sorted.length > 0) {
        const latest = sorted[0];
        const hasVitals = [
          latest.systolicBP, latest.diastolicBP, latest.pulse,
          latest.temperature, latest.spO2, latest.respiratoryRate, latest.weight,
        ].some((v) => v !== null && v !== undefined);
        onLatestVitalsStatus(hasVitals);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load vitals history';
      setError(errorMsg);
      safeConsole.error('Failed to load vitals history:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (visit: VisitWithVitals) => {
    setEditingVisit(visit);
    setEditingVisitId(visit.id);
    setExpandedId(visit.id);
    setEditErrors({});
    setEditReason({ category: '', detail: '' });
    // Populate form with current visit data
    setFormData({
      systolicBP: visit.systolicBP ? String(visit.systolicBP) : '',
      diastolicBP: visit.diastolicBP ? String(visit.diastolicBP) : '',
      pulse: visit.pulse ? String(visit.pulse) : '',
      temperature: visit.temperature ? String(visit.temperature) : '',
      spO2: visit.spO2 ? String(visit.spO2) : '',
      respiratoryRate: visit.respiratoryRate ? String(visit.respiratoryRate) : '',
      weight: visit.weight ? String(visit.weight) : '',
      duration: visit.duration || '',
      currentMedications: visit.currentMedications || '',
      clinicalNotes: visit.clinicalNotes || '',
    });
    try {
      setExtraFieldValues(visit.structuredDataJson ? JSON.parse(visit.structuredDataJson) : {});
    } catch {
      setExtraFieldValues({});
    }
  };

  const handleEditCancel = () => {
    setEditingVisitId(null);
    setEditingVisit(null);
    setEditErrors({});
    setEditReason({ category: '', detail: '' });
    setExtraFieldValues({});
  };

  const validateEditForm = () => {
    const newErrors: {[key: string]: string} = {};

    // Reason is only required when editing existing vitals, not for initial entry
    const isInitialEntry = editingVisit && isInitialVitalsEntry(editingVisit);

    if (!isInitialEntry) {
      if (!editReason.category) {
        newErrors.category = 'Please select a reason category';
      }

      if (!editReason.detail || editReason.detail.trim().length === 0) {
        newErrors.detail = 'Please provide details about why you are editing';
      }

      if (editReason.detail && editReason.detail.length > 500) {
        newErrors.detail = 'Reason details must be 500 characters or less';
      }
    }

    setEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEditSave = async () => {
    if (!validateEditForm() || !editingVisitId) return;

    setEditLoading(true);
    try {
      const vitalsData = {
        systolicBP: formData.systolicBP ? parseInt(formData.systolicBP) : null,
        diastolicBP: formData.diastolicBP ? parseInt(formData.diastolicBP) : null,
        pulse: formData.pulse ? parseInt(formData.pulse) : null,
        temperature: formData.temperature ? parseFloat(formData.temperature) : null,
        spO2: formData.spO2 ? parseInt(formData.spO2) : null,
        respiratoryRate: formData.respiratoryRate ? parseInt(formData.respiratoryRate) : null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
      };

      // Initial entry has no reason to report — sending "" would fail server-side enum parsing,
      // and there's nothing to explain yet anyway (see isInitialVitalsEntry).
      const isInitialEntry = editingVisit && isInitialVitalsEntry(editingVisit);
      const response = await api.visits.updateVitals(
        editingVisitId,
        vitalsData,
        isInitialEntry ? undefined : editReason.category,
        isInitialEntry ? undefined : editReason.detail,
        encounterTemplate ? encodeEncounterFieldValues(extraFieldValues) : undefined
      );

      if (response.error) {
        throw new Error(response.error);
      }

      toast.success('Vitals updated successfully');
      handleEditCancel();
      loadVitalsHistory();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update vitals';
      toast.error(errorMsg);
      safeConsole.error('Failed to update vitals:', err);
    } finally {
      setEditLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (vitalsHistory.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <AlignLeft size={40} style={{ color: '#ccc', marginBottom: 16 }} />
        <Typography variant="h6" color="textSecondary">
          No vitals recorded yet
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 2.5 }}>
      {/* Single Add/Edit Vitals button — targets the most recent visit */}
      {vitalsHistory.length > 0 && (() => {
        const latest = vitalsHistory[0];
        const hasAnyVitals = [
          latest.systolicBP, latest.diastolicBP, latest.pulse,
          latest.temperature, latest.spO2, latest.respiratoryRate, latest.weight,
        ].some((v) => v !== null && v !== undefined);
        const canEditVitals = appointmentStatus === 'WAITING' || appointmentStatus === 'IN_PROGRESS';
        if (!canEditVitals || hasAnyVitals) return null;
        return (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<Plus size={16} />}
              onClick={() => handleEditClick(latest)}
            >
              Add Vitals
            </Button>
          </Box>
        );
      })()}
      <Stack spacing={1.5}>
        {vitalsHistory.map((visit, index) => {
          const isEditing = editingVisitId === visit.id;
          const hasAnyVitals = [
            visit.systolicBP, visit.diastolicBP, visit.pulse,
            visit.temperature, visit.spO2, visit.respiratoryRate, visit.weight,
          ].some((v) => v !== null && v !== undefined);
          const isCurrentVisit = index === 0;
          const canEdit = isCurrentVisit && hasAnyVitals &&
            (appointmentStatus === 'WAITING' || appointmentStatus === 'IN_PROGRESS');
          const summaryChips = compactVitals(visit);
          const isExpanded = isEditing || expandedId === visit.id;
          return (
          <Accordion
            key={visit.id}
            expanded={isExpanded}
            onChange={(_, exp) => { if (!isEditing) setExpandedId(exp ? visit.id : null); }}
            disableGutters
            sx={{
              border: '1px solid',
              borderColor: isCurrentVisit ? 'primary.light' : 'divider',
              borderRadius: 2,
              boxShadow: 'none',
              overflow: 'hidden',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ChevronDown size={18} />}
              sx={{
                bgcolor: isCurrentVisit ? 'primary.50' : 'action.hover',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center', justifyContent: 'space-between',
                  gap: 1, flexWrap: 'wrap', my: 1, mr: 1,
                },
              }}
            >
              <Box sx={{ minWidth: 140 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {new Date(visit.visitDate).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'short', day: 'numeric',
                    })}
                  </Typography>
                  {isCurrentVisit && (
                    <Chip label="Latest" size="small" color="primary"
                      sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {visit.doctorName || 'Doctor'}{visit.totalEdits ? ` • edited ${visit.totalEdits}×` : ''}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap
                sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
                {summaryChips.length === 0 ? (
                  <Chip label="No vitals" size="small" variant="outlined"
                    sx={{ height: 22, fontSize: '0.68rem', color: 'text.secondary' }} />
                ) : (
                  summaryChips.map((c) => (
                    <Chip key={c} label={c} size="small" variant="outlined"
                      sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: 'background.paper' }} />
                  ))
                )}
                {canEdit && !isEditing && (
                  <Button size="small" startIcon={<Edit2 size={14} />}
                    onClick={(e) => { e.stopPropagation(); handleEditClick(visit); }}
                    sx={{ ml: 0.5, minWidth: 0 }}>
                    Edit
                  </Button>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 1.5 }}>
              {isEditing ? (
                // INLINE EDIT FORM
                <Stack spacing={2.5}>
                  {/* Edit Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Edit Vitals - {new Date(visit.visitDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </Typography>
                  </Box>

                  {/* Vital Fields Grid */}
                  <Grid container spacing={1.5}>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth size="small" label="Systolic BP" type="number"
                        value={formData.systolicBP}
                        onChange={(e) => setFormData(prev => ({ ...prev, systolicBP: e.target.value }))}
                        InputProps={{ endAdornment: 'mmHg' }}
                      />
                      <IndicatorBadge field="systolicBP" value={formData.systolicBP} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth size="small" label="Diastolic BP" type="number"
                        value={formData.diastolicBP}
                        onChange={(e) => setFormData(prev => ({ ...prev, diastolicBP: e.target.value }))}
                        InputProps={{ endAdornment: 'mmHg' }}
                      />
                      <IndicatorBadge field="diastolicBP" value={formData.diastolicBP} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth size="small" label="Pulse" type="number"
                        value={formData.pulse}
                        onChange={(e) => setFormData(prev => ({ ...prev, pulse: e.target.value }))}
                        InputProps={{ endAdornment: 'bpm' }}
                      />
                      <IndicatorBadge field="pulse" value={formData.pulse} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth size="small" label="Temperature" type="number"
                        inputProps={{ step: '0.1' }}
                        value={formData.temperature}
                        onChange={(e) => setFormData(prev => ({ ...prev, temperature: e.target.value }))}
                        InputProps={{ endAdornment: '°F' }}
                      />
                      <IndicatorBadge field="temperature" value={formData.temperature} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth size="small" label="SpO₂" type="number"
                        value={formData.spO2}
                        onChange={(e) => setFormData(prev => ({ ...prev, spO2: e.target.value }))}
                        InputProps={{ endAdornment: '%' }}
                      />
                      <IndicatorBadge field="spO2" value={formData.spO2} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth size="small" label="RR" type="number"
                        value={formData.respiratoryRate}
                        onChange={(e) => setFormData(prev => ({ ...prev, respiratoryRate: e.target.value }))}
                        InputProps={{ endAdornment: '/min' }}
                      />
                      <IndicatorBadge field="respiratoryRate" value={formData.respiratoryRate} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth size="small" label="Weight" type="number"
                        inputProps={{ step: '0.1' }}
                        value={formData.weight}
                        onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                        InputProps={{ endAdornment: 'kg' }}
                      />
                    </Grid>
                  </Grid>

                  {encounterTemplate && (
                    <DynamicEncounterFields
                      label={encounterTemplate.label}
                      vitalsExtraFields={encounterTemplate.vitalsExtraFields}
                      noteExtraFields={encounterTemplate.noteExtraFields}
                      values={extraFieldValues}
                      onChange={(key, value) => setExtraFieldValues((prev) => ({ ...prev, [key]: value }))}
                      disabled={editLoading}
                    />
                  )}

                  {/* Edit Reason — only meaningful when correcting an already-recorded value;
                      a first-time entry has nothing prior to explain changing. */}
                  {editingVisit && !isInitialVitalsEntry(editingVisit) && (
                    <>
                      <Divider />

                      <FormControl fullWidth error={!!editErrors.category}>
                        <FormLabel sx={{ mb: 1, fontWeight: 600 }}>Why are you editing? *</FormLabel>
                        <TextField
                          select
                          size="small"
                          value={editReason.category}
                          onChange={(e) => {
                            setEditReason(prev => ({ ...prev, category: e.target.value }));
                            setEditErrors(prev => ({ ...prev, category: '' }));
                          }}
                        >
                          <MenuItem value="">Select reason</MenuItem>
                          {EDIT_REASON_CATEGORIES.map(cat => (
                            <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                          ))}
                        </TextField>
                        {editErrors.category && <FormHelperText>{editErrors.category}</FormHelperText>}
                      </FormControl>

                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Details (required)"
                        value={editReason.detail}
                        onChange={(e) => {
                          setEditReason(prev => ({ ...prev, detail: e.target.value }));
                          setEditErrors(prev => ({ ...prev, detail: '' }));
                        }}
                        error={!!editErrors.detail}
                        helperText={editErrors.detail || `${editReason.detail.length}/500`}
                      />
                    </>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<X size={16} />}
                      onClick={handleEditCancel}
                      disabled={editLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Save size={16} />}
                      onClick={handleEditSave}
                      disabled={editLoading}
                    >
                      {editLoading ? 'Saving...' : 'Save'}
                    </Button>
                  </Box>
                </Stack>
              ) : (
                <>
              {/* Vital Signs Grid */}
              <>
                    <SectionLabel icon={<Activity size={13} />} label="Vital Signs" color="#1976d2" />
                    {!hasAnyVitals ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                        No vitals recorded for this visit
                      </Typography>
                    ) : (
                      <Grid container spacing={1.5} sx={{ mb: 2 }}>
                        {visit.systolicBP != null && (
                          <VitalTile visitId={visit.id} field="systolicBP" label="SBP" value={visit.systolicBP} unit="mmHg" />
                        )}
                        {visit.diastolicBP != null && (
                          <VitalTile visitId={visit.id} field="diastolicBP" label="DBP" value={visit.diastolicBP} unit="mmHg" />
                        )}
                        {visit.pulse != null && (
                          <VitalTile visitId={visit.id} field="pulse" label="Pulse" value={visit.pulse} unit="bpm" />
                        )}
                        {visit.temperature != null && (
                          <VitalTile visitId={visit.id} field="temperature" label="Temp" value={visit.temperature} unit="°F" />
                        )}
                        {visit.spO2 != null && (
                          <VitalTile visitId={visit.id} field="spO2" label="SpO₂" value={visit.spO2} unit="%" />
                        )}
                        {visit.respiratoryRate != null && (
                          <VitalTile visitId={visit.id} field="respiratoryRate" label="RR" value={visit.respiratoryRate} unit="/min" />
                        )}
                        {visit.weight != null && (
                          <VitalTile visitId={visit.id} field="weight" label="Weight" value={visit.weight} unit="kg" />
                        )}
                      </Grid>
                    )}
                  </>

              {/* Patient Context */}
              {(visit.chiefComplaint || visit.duration || visit.currentMedications) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <SectionLabel icon={<ClipboardList size={13} />} label="Patient Context" color="#0288d1" />
                  <Stack spacing={0.5}>
                    {visit.chiefComplaint && (
                      <Typography variant="body2">
                        <strong>Chief Complaint:</strong> {visit.chiefComplaint}
                      </Typography>
                    )}
                    {visit.duration && (
                      <Typography variant="body2">
                        <strong>Duration:</strong> {visit.duration}
                      </Typography>
                    )}
                    {visit.currentMedications && (
                      <Typography variant="body2">
                        <strong>Current Medications:</strong> {visit.currentMedications}
                      </Typography>
                    )}
                  </Stack>
                </>
              )}

              {/* Clinical Notes */}
              {visit.clinicalNotes && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <SectionLabel icon={<StickyNote size={13} />} label="Clinical Notes" color="#2e7d32" />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                    {visit.clinicalNotes}
                  </Typography>
                </>
              )}

              {/* Specialty extra fields (GYNECOLOGY_OB/PEDIATRICS answers) */}
              {visit.structuredDataJson && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <SectionLabel icon={<Stethoscope size={13} />} label="Specialty Details" color="#7b1fa2" />
                  <Stack spacing={0.5}>
                    {(() => {
                      try {
                        const parsed = JSON.parse(visit.structuredDataJson) as Record<string, string>;
                        return Object.entries(parsed).map(([key, value]) => (
                          <Typography key={key} variant="body2">
                            <strong>{key}:</strong> {value}
                          </Typography>
                        ));
                      } catch {
                        return null;
                      }
                    })()}
                  </Stack>
                </>
              )}
              </>
              )}
            </AccordionDetails>
          </Accordion>
          );
        })}
      </Stack>
    </Box>
  );
}

export default VisitVitalsHistoryTab;
